import React, { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useSysTheme, useDisplayTheme } from 'hooks/useUiState'
import {
  Cross
  // @ts-ignore
} from '@netbasequid/core'
import ScrollbarStyle from 'styles/ScrollbarStyle'
import { Theme } from 'features/ui/types'
import { SystemTheme } from 'hooks/useUiState/types'
import { TimelineAnnotation } from 'schemas/TimelineAnnotationSchema'
import useRedrawId from 'hooks/useRedrawId'
import { AUTO_ATTR_KEY, AutomationId } from 'utils/automationUtil'
import AnnotationCard from './Card'
import EditCard from './EditCard'
import InformationCard from './InformationCard'

const AnnotationWrapper = styled.div.attrs({
  [AUTO_ATTR_KEY]: AutomationId.annotation.list
})<{ $theme: SystemTheme }>`
  width: 270px;
  background-color: ${({ $theme }) => $theme.elevation[0].background};
  color: ${({ $theme }) => $theme.text.primary};
  border: 1px solid ${({ $theme }) => $theme.common.divider[100]};

  display: flex;
  flex-direction: column;
`

const TitleContainer = styled.div<{ $theme: SystemTheme }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0 8px;
  margin: 0 16px;
  border-bottom: 1px solid ${({ $theme }) => $theme.common.divider[100]};
  & > span {
    font-size: ${({ $theme }) => $theme.typescale[16].size};
    line-height: ${({ $theme }) => $theme.typescale[16].lineHeight};
    font-weight: ${({ $theme }) => $theme.fontWeight.semibold};
  }
  & > div:last-child {
    cursor: pointer;
  }
`
const CardContainer = styled.div<{ $mode: Theme }>`
  flex: 1;
  overflow-y: auto;
  ${ScrollbarStyle}
`

const Annotation = ({
  annotations = [],
  onClose,
  onDelete,
  onUpdate,
  onAdd,
  isOperating,
  operatingId,
  operable
}: {
  annotations?: TimelineAnnotation[]
  onClose?: () => void
  onDelete?: (annotation: TimelineAnnotation) => void
  onUpdate?: (annotation: TimelineAnnotation) => void
  onAdd?: (annotation: Pick<TimelineAnnotation, 'color' | 'description'>) => void
  isOperating?: boolean
  operatingId?: number
  operable?: boolean
}) => {
  const theme = useSysTheme()
  const mode = useDisplayTheme()
  const { t } = useTranslation()
  const [editId, setEditId] = useState<number | null>(null)
  const { redrawId, updateRedrawId } = useRedrawId()

  const handleUpdate = async (annotation: TimelineAnnotation) => {
    await onUpdate?.(annotation)
    setEditId(null)
  }

  const handleAdd = async (annotation: Pick<TimelineAnnotation, 'color' | 'description'>) => {
    await onAdd?.(annotation)
    updateRedrawId()
  }

  return (
    <AnnotationWrapper $theme={theme}>
      <TitleContainer $theme={theme}>
        <span>{t('annotation')}</span>
        <div
          role="button"
          tabIndex={0}
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onClose?.()
            }
          }}>
          <Cross $mode={mode} />
        </div>
      </TitleContainer>
      <CardContainer $mode={mode}>
        {!operable || annotations.length >= 200 ? (
          <InformationCard
            message={!operable ? t('cantAddEditAnnotation') : t('cantAddCauseMaximumAnnotation')}
          />
        ) : (
          <EditCard onAdd={handleAdd} isSaving={isOperating && !operatingId} key={redrawId} />
        )}
        {annotations.map((annotation) =>
          annotation.id === editId ? (
            <EditCard
              key={annotation.id}
              annotation={annotation}
              onCancel={() => setEditId(null)}
              onUpdate={handleUpdate}
              isSaving={isOperating && operatingId === annotation.id}
            />
          ) : (
            <AnnotationCard
              key={annotation.id}
              annotation={annotation}
              onEdit={(annotationId) => setEditId(annotationId)}
              onDelete={onDelete}
              isDeleting={isOperating && operatingId === annotation.id}
              operable={operable}
            />
          )
        )}
      </CardContainer>
    </AnnotationWrapper>
  )
}

export default Annotation
